import * as THREE from "three";
import type { LevelData } from "../world/level";
import type { Director } from "../ai/enemy";
import type { Player } from "../player/player";
import type { AudioSys } from "../engine/audio";

export type Phase = "apron" | "plant" | "beacon" | "hold" | "extract" | "done";

export class Mission {
  phase: Phase = "apron";
  holdT = 0;
  plantT = 0;
  planting = false;
  startedAt = 0;
  stats = { kills: 0, shots: 0, hits: 0, time: 0 };

  constructor(private level: LevelData) {}

  start() {
    this.phase = "apron";
    this.holdT = 0;
    this.plantT = 0;
    this.planting = false;
    this.startedAt = performance.now() / 1000;
    this.stats = { kills: 0, shots: 0, hits: 0, time: 0 };
    for (const it of this.level.interacts) it.done = false;
  }

  objective() {
    switch (this.phase) {
      case "apron":
        return { main: "CLEAR THE CONTAINER APRON", sub: "NEUTRALIZE VESPER ON THE DOCKS" };
      case "plant":
        return { main: "BREACH PROCESSING", sub: "PUSH THROUGH THE HALL TO THE STACK" };
      case "beacon":
        return { main: "PLANT TARGETING BEACON", sub: "HOLD F ON THE HELIPAD" };
      case "hold":
        return { main: "HOLD THE PAD", sub: `${Math.ceil(Math.max(0, 25 - this.holdT))}s UNTIL EXTRACT` };
      case "extract":
        return { main: "GET TO THE BIRD", sub: "STAND ON THE PAD" };
      case "done":
        return { main: "EXTRACT COMPLETE", sub: "TERMINAL 9 IS MARKED" };
    }
  }

  update(dt: number, player: Player, director: Director, audio: AudioSys, interactHeld: boolean) {
    const now = performance.now() / 1000;
    this.stats.time = now - this.startedAt;
    this.stats.kills = director.kills;
    const z = player.mover.pos.z;
    const living = director.living();
    const apronClear = living.filter((e) => e.pos.z < 55).length === 0;
    const plantClear = living.filter((e) => e.pos.z >= 55 && e.pos.z < 86).length <= 1;

    if (this.phase === "apron" && (apronClear || z > 56)) {
      this.phase = "plant";
      audio.play("ui2", { vol: 0.5 });
      return "APRON CLEAR — PUSH THE PLANT";
    }
    if (this.phase === "plant" && (plantClear || z > 84)) {
      this.phase = "beacon";
      audio.play("ui2", { vol: 0.5 });
      return "REACH THE PAD — PLANT THE BEACON";
    }
    if (this.phase === "beacon") {
      const it = this.level.interacts.find((i) => i.id === "beacon")!;
      const near = player.mover.pos.distanceTo(new THREE.Vector3(it.pos.x, player.mover.pos.y, it.pos.z)) < it.radius && player.mover.pos.y > 7.2;
      if (near && interactHeld) {
        this.planting = true;
        this.plantT += dt;
        if (Math.floor(this.plantT * 6) !== Math.floor((this.plantT - dt) * 6)) audio.play("plant", { vol: 0.4 });
        if (this.plantT >= 2.6) {
          it.done = true;
          this.phase = "hold";
          this.holdT = 0;
          audio.play("ui2", { vol: 0.6 });
          this.wakeRemainders(director, player);
          return "BEACON LIVE — HOLD FOR EXTRACT";
        }
      } else {
        this.planting = false;
        this.plantT = Math.max(0, this.plantT - dt * 0.8);
      }
    }
    if (this.phase === "hold") {
      this.holdT += dt;
      if (this.holdT >= 25) {
        this.phase = "extract";
        audio.play("ui2", { vol: 0.6 });
        return "BIRD INBOUND — GET ON THE PAD";
      }
    }
    if (this.phase === "extract") {
      const onPad =
        Math.abs(player.mover.pos.x - this.level.extract.x) < 5 &&
        Math.abs(player.mover.pos.z - this.level.extract.z) < 5 &&
        player.mover.pos.y > 7.5;
      if (onPad) {
        this.phase = "done";
        audio.play("ui2", { vol: 0.7 });
        return "EXTRACT COMPLETE";
      }
    }
    return null;
  }

  interactHint(player: Player) {
    if (this.phase !== "beacon") return null;
    const it = this.level.interacts.find((i) => i.id === "beacon")!;
    if (it.done) return null;
    const near =
      player.mover.pos.distanceTo(new THREE.Vector3(it.pos.x, player.mover.pos.y, it.pos.z)) < it.radius &&
      player.mover.pos.y > 7.2;
    if (!near) return null;
    const pct = Math.floor((this.plantT / 2.6) * 100);
    return this.planting ? `PLANTING ${pct}%` : it.label;
  }

  private wakeRemainders(director: Director, player: Player) {
    for (const e of director.enemies) {
      if (e.state === "dead") continue;
      e.state = "combat";
      e.alert = 10;
      e.lastKnown.copy(player.camera.position);
    }
  }
}
